// ─────────────────────────────────────────────────────────────────────────────
// BizPlus parity — configurable document numbering series (Phase 0.4)
//
// Replaces the earlier local-state-only mock (a single prefix/pattern preview
// that saved nothing) with a real table backed by /api/numbering/series: one
// row per document type, each independently editable and saved, with a
// client-computed live preview so admins see the exact next-number format
// before committing.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { Hash, CheckCircle2 } from 'lucide-react';

interface SeriesRow {
  documentType: string;
  prefix: string;
  suffix: string;
  startNo: number;
  currentNo: number;
  yearMode: 'none' | 'calendar' | 'financial';
  financialYearStartMonth: number;
  yearFormat: 'full' | 'short';
  padding: number;
  allowManualEdit: boolean;
  resetOn: 'never' | 'yearly';
  isActive: boolean;
  unsaved?: boolean;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  service_ticket: 'Service Complaint (CMS)',
  quotation: 'Quotation',
  proforma: 'Proforma Invoice',
  sales_order: 'Sales Order',
  direct_sale: 'Direct Sale',
  delivery_challan: 'Delivery Challan',
  // The GST tax invoice — previously not configurable anywhere; every one was
  // numbered by a separate, fixed-format counter with no admin control.
  sales_invoice: 'Tax Invoice',
  sample_invoice: 'Sample Invoice',
  sales_return: 'Sales Return',
  purchase_order: 'Purchase Order',
  purchase_return: 'Purchase Return',
  purchase_receipt: 'Purchase Receipt (GRN)',
};

/** Apr(4)-start financial year label — mirrors src/numbering-allocator.js's
 *  resolveFinancialYear() exactly, so the preview matches what saving would
 *  actually produce without a round trip to the server on every keystroke.
 *  'full': 2026-27 (this system's own default). 'short': 26-27 — RBM's own
 *  pre-existing convention on every document they numbered before this
 *  settings screen existed. */
function resolveFinancialYear(date: Date, startMonth = 4, format: 'full' | 'short' = 'full'): string {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const fyStartYear = month >= startMonth ? year : year - 1;
  const startPart = format === 'short' ? String(fyStartYear % 100).padStart(2, '0') : String(fyStartYear);
  return `${startPart}-${String((fyStartYear + 1) % 100).padStart(2, '0')}`;
}

function previewFor(row: SeriesRow): string {
  const yearLabel =
    row.yearMode === 'financial' ? resolveFinancialYear(new Date(), row.financialYearStartMonth, row.yearFormat)
    : row.yearMode === 'calendar' ? (row.yearFormat === 'short' ? String(new Date().getFullYear() % 100).padStart(2, '0') : String(new Date().getFullYear()))
    : '';
  const seq = String(Math.max(row.currentNo + 1, row.startNo)).padStart(row.padding, '0');
  const parts = [row.prefix, yearLabel, seq].filter((p) => p !== '' && p != null);
  return [parts.join('/'), row.suffix].filter(Boolean).join('-');
}

export function NumberingSettingsPage() {
  const [rows, setRows] = useState<SeriesRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchSeries = () => {
    setLoading(true);
    fetch('/api/numbering/series')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.series)) setRows(data.series);
      })
      .catch(() => setError('Could not load numbering series.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSeries();
  }, []);

  const updateRow = (documentType: string, patch: Partial<SeriesRow>) => {
    setRows((prev) => prev.map((r) => (r.documentType === documentType ? { ...r, ...patch } : r)));
  };

  const saveRow = async (row: SeriesRow) => {
    // Saving Start No. now moves the live counter to match, in either
    // direction — including backward. That's safe for a series nothing has
    // been issued under yet, but on one already in real use it means the
    // next number issued could repeat one already printed on a real
    // document, so a deliberate lower move gets a confirmation first.
    if (row.startNo - 1 < row.currentNo) {
      const ok = window.confirm(
        `This moves the ${DOCUMENT_TYPE_LABELS[row.documentType] || row.documentType} counter backward, from ${row.currentNo} to ${row.startNo - 1}. ` +
        `The next number issued will be ${row.startNo} — if any document was already issued using that number or higher, this will cause a repeat. Continue?`
      );
      if (!ok) return;
    }
    setSavingType(row.documentType);
    setError(null);
    try {
      const res = await fetch(`/api/numbering/series/${row.documentType}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prefix: row.prefix,
          suffix: row.suffix,
          startNo: row.startNo,
          padding: row.padding,
          yearMode: row.yearMode,
          financialYearStartMonth: row.financialYearStartMonth,
          yearFormat: row.yearFormat,
          allowManualEdit: row.allowManualEdit,
          resetOn: row.resetOn,
          isActive: row.isActive,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed.');
      updateRow(row.documentType, { ...data.series, unsaved: false });
      setToast(`${DOCUMENT_TYPE_LABELS[row.documentType]} series saved.`);
      setTimeout(() => setToast(null), 2500);
    } catch (err: any) {
      setError(err.message || 'Could not save this series.');
    } finally {
      setSavingType(null);
    }
  };

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <SettingsSubNav />

      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        <div>
          <h2 className="text-lg font-bold">Document Number Sequences</h2>
          <p className="text-xs text-slate-500">
            One configurable series per document type — prefix, suffix, padding, financial-year stamping and
            manual-edit permission. Allocations use MongoDB atomic <code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">$inc</code> so
            two people confirming documents at the same moment can never receive the same number.
          </p>
        </div>

        {toast && (
          <div className="p-4 bg-success/10 dark:bg-success border border-success/25 dark:border-success/40 rounded-xl text-success-text dark:text-success-text text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success-text" />
            {toast}
          </div>
        )}
        {error && (
          <div className="p-4 bg-danger/10 dark:bg-danger border border-danger/25 dark:border-danger/40 rounded-xl text-danger-text dark:text-danger-text text-sm">
            {error}
          </div>
        )}

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading numbering series...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 font-semibold text-slate-900 dark:text-slate-100 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3">Document Type</th>
                    <th className="px-3 py-3">Prefix</th>
                    <th className="px-3 py-3">Suffix</th>
                    <th className="px-3 py-3">Start No.</th>
                    <th className="px-3 py-3">Padding</th>
                    <th className="px-3 py-3">Year</th>
                    <th className="px-3 py-3">Year Format</th>
                    <th className="px-3 py-3">Reset</th>
                    <th className="px-3 py-3">Manual Edit</th>
                    <th className="px-4 py-3 flex items-center gap-1"><Hash className="w-3.5 h-3.5" /> Next Number</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {rows.map((row) => (
                    <tr key={row.documentType} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-medium">
                        {DOCUMENT_TYPE_LABELS[row.documentType] || row.documentType}
                        {row.unsaved && <span className="ml-2 text-[10px] uppercase font-bold text-slate-400">using defaults</span>}
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={row.prefix}
                          onChange={(e) => updateRow(row.documentType, { prefix: e.target.value.toUpperCase().slice(0, 20) })}
                          className="w-20 px-2 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono uppercase"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={row.suffix}
                          onChange={(e) => updateRow(row.documentType, { suffix: e.target.value.slice(0, 20) })}
                          className="w-16 px-2 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min={1}
                          value={row.startNo}
                          onChange={(e) => updateRow(row.documentType, { startNo: Math.max(1, Number(e.target.value) || 1) })}
                          className="w-20 px-2 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={row.padding}
                          onChange={(e) => updateRow(row.documentType, { padding: Math.min(10, Math.max(1, Number(e.target.value) || 1)) })}
                          className="w-16 px-2 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={row.yearMode}
                          onChange={(e) => updateRow(row.documentType, { yearMode: e.target.value as SeriesRow['yearMode'] })}
                          className="px-2 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                        >
                          <option value="none">None</option>
                          <option value="calendar">Calendar</option>
                          <option value="financial">Financial (Apr–Mar)</option>
                        </select>
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={row.yearFormat}
                          disabled={row.yearMode === 'none'}
                          onChange={(e) => updateRow(row.documentType, { yearFormat: e.target.value as SeriesRow['yearFormat'] })}
                          className="px-2 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm disabled:opacity-50"
                          title="Short matches RBM's own existing document numbers (e.g. RBM/26-27/1987)"
                        >
                          <option value="full">2026-27</option>
                          <option value="short">26-27</option>
                        </select>
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={row.resetOn}
                          onChange={(e) => updateRow(row.documentType, { resetOn: e.target.value as SeriesRow['resetOn'] })}
                          className="px-2 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                        >
                          <option value="yearly">Yearly</option>
                          <option value="never">Never</option>
                        </select>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={row.allowManualEdit}
                          onChange={(e) => updateRow(row.documentType, { allowManualEdit: e.target.checked })}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-indigo-700 dark:text-indigo-400 whitespace-nowrap">
                        {previewFor(row)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => saveRow(row)}
                          disabled={savingType === row.documentType}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-xs rounded-lg shadow-sm whitespace-nowrap"
                        >
                          {savingType === row.documentType ? 'Saving…' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NumberingSettingsPage;
